import { useState } from "react";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";
import { CheckCircle, Warning, Prohibit } from "@phosphor-icons/react";

const OUTCOMES = [
  { key: "safe_to_proceed", label: "Safe to proceed", icon: CheckCircle, cls: "border-emerald-500 bg-emerald-50 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200" },
  { key: "proceed_with_controls", label: "Proceed with extra controls", icon: Warning, cls: "border-amber-500 bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200" },
  { key: "stop_work", label: "Stop work — unsafe", icon: Prohibit, cls: "border-red-600 bg-red-50 text-red-900 dark:bg-red-950/40 dark:text-red-200" },
];

function getLocation() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve({});
    navigator.geolocation.getCurrentPosition((p) => resolve({ latitude: p.coords.latitude, longitude: p.coords.longitude }), () => resolve({}), { timeout: 6000 });
  });
}

/** Point-of-work dynamic risk assessment: every question answered, 'No' needs a note and forces extra controls or stop work. */
export default function DraForm({ entityType, entityId, questions, ramsId, onSubmitted }) {
  const [ans, setAns] = useState({});
  const [hazards, setHazards] = useState("");
  const [controls, setControls] = useState("");
  const [outcome, setOutcome] = useState("");
  const [busy, setBusy] = useState(false);

  const setA = (key, patch) => setAns(a => ({ ...a, [key]: { ...(a[key] || {}), ...patch } }));
  const risks = questions.filter(q => ans[q.key]?.answer === "no").length;
  const allAnswered = questions.every(q => ans[q.key]?.answer);
  const effective = outcome || (allAnswered ? (risks ? "proceed_with_controls" : "safe_to_proceed") : "");

  const submit = async () => {
    if (!allAnswered) return toast.error("Answer every question first");
    if (risks && effective === "safe_to_proceed") return toast.error("You flagged a risk — choose extra controls or stop work");
    if (effective === "proceed_with_controls" && !controls.trim()) return toast.error("Describe the extra controls you are putting in place");
    setBusy(true);
    try {
      const coords = await getLocation();
      const { data } = await API.post(`/in-house-portal/${entityType}/${entityId}/dra`, {
        answers: questions.map(q => ({ key: q.key, answer: ans[q.key].answer, note: ans[q.key].note || "" })),
        hazards_noted: hazards, controls, outcome: effective, rams_id: ramsId || "", ...coords,
      });
      toast.success(effective === "stop_work" ? "Stop work recorded — the office has been alerted" : "Risk assessment recorded");
      onSubmitted(data);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="dra-form">
      <ul className="divide-y divide-border border border-border rounded-md">
        {questions.map((q, i) => {
          const a = ans[q.key] || {};
          return (
            <li key={q.key} className="p-2.5" data-testid={`dra-q-${q.key}`}>
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-xs flex-1 min-w-[200px]"><span className="text-muted-foreground mr-1">{i + 1}.</span>{q.text}</p>
                <div className="flex gap-1">
                  {[["yes", "Yes", "bg-emerald-600 border-emerald-600 text-white"], ["no", "No", "bg-red-600 border-red-600 text-white"], ["na", "N/A", "bg-slate-600 border-slate-600 text-white"]].map(([v, l, on]) => (
                    <button key={v} type="button" onClick={() => setA(q.key, { answer: v })} data-testid={`dra-q-${q.key}-${v}`}
                      className={`text-xs px-3 py-1.5 rounded-md border min-w-[48px] font-semibold transition-colors ${a.answer === v ? on : "border-border hover:bg-muted"}`}>{l}</button>
                  ))}
                </div>
              </div>
              {a.answer === "no" && <input value={a.note || ""} onChange={e => setA(q.key, { note: e.target.value })} placeholder="What's wrong?" className="mt-2 w-full h-8 text-xs rounded border border-red-300 bg-background px-2" data-testid={`dra-q-${q.key}-note`} />}
            </li>
          );
        })}
      </ul>
      <div>
        <Label className="text-[11px]">Anything else you've spotted on site</Label>
        <Textarea value={hazards} onChange={e => setHazards(e.target.value)} rows={2} className="mt-1 text-xs" placeholder="New hazards, changed conditions, concerns…" data-testid="dra-hazards" />
      </div>
      <div className="grid sm:grid-cols-3 gap-2">
        {OUTCOMES.map(o => {
          const Icon = o.icon; const on = effective === o.key;
          return (
            <button key={o.key} type="button" onClick={() => setOutcome(o.key)} data-testid={`dra-outcome-${o.key}`}
              className={`flex items-center gap-2 p-2.5 rounded-md border-2 text-left text-xs font-semibold transition-colors ${on ? o.cls : "border-border text-muted-foreground hover:bg-muted"}`}>
              <Icon size={18} weight={on ? "fill" : "regular"} /> {o.label}
            </button>
          );
        })}
      </div>
      {effective === "proceed_with_controls" && (
        <div>
          <Label className="text-[11px]">Extra controls you are putting in place</Label>
          <Textarea value={controls} onChange={e => setControls(e.target.value)} rows={2} className="mt-1 text-xs border-amber-400" data-testid="dra-controls" />
        </div>
      )}
      {effective === "stop_work" && <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">Stop work alerts the office immediately. Make the area safe, step away and call your supervisor.</p>}
      <Button onClick={submit} disabled={busy || !allAnswered} className="w-full sm:w-auto" data-testid="dra-submit">{busy ? "Saving…" : "Submit risk assessment"}</Button>
    </div>
  );
}
